const CONTENT_TYPE_PREFIX = {
  lesson: "Lesson",
  scenario: "Roleplay",
  "info-page": "Info Page",
};

/** Default title for new composer entities until the author names them (enables create + autosave). */
export const COMPOSER_DEFAULT_NEW_ENTITY_NAME = "Untitled";

export const normalizeContentEditorLanguage = (lang) => {
  const key = String(lang || "").trim().toLowerCase();
  if (!key) return "";
  if (key === "en" || key === "english") return "english";
  if (key === "he" || key === "hebrew") return "hebrew";
  return key;
};

const normalizeLanguageKey = (lang) => normalizeContentEditorLanguage(lang);

const getI18nValueForLanguage = (i18nMap, lang) => {
  if (!i18nMap || typeof i18nMap !== "object") return undefined;
  const target = normalizeLanguageKey(lang);
  if (!target) return undefined;
  const aliases = target === "english"
    ? new Set(["english", "en"])
    : target === "hebrew"
      ? new Set(["hebrew", "he"])
      : new Set([target]);

  for (const [key, value] of Object.entries(i18nMap)) {
    if (!String(value || "").trim()) continue;
    if (aliases.has(normalizeLanguageKey(key))) {
      return value;
    }
  }

  return undefined;
};

/**
 * The server's tie-break for two children of one unit, from `main/models/unit_tree.py`. Ids come
 * from separate tables, so a lesson and a role play can share both `order` and `id`; then only the
 * kind decides, and the editor has to pick the same one the learner's page does.
 */
const CHILD_KIND_RANK = { scenario: 0, lesson: 1, "info-page": 2, unit: 3 };

/** Units among units: `(order, id)`, the order the course page lists its sessions in. */
export const sortByOrder = (items = []) =>
  [...items]
    .filter((item) => item?.id != null)
    .sort(
      (a, b) =>
        Number(a?.order || 0) - Number(b?.order || 0) ||
        Number(a?.id || 0) - Number(b?.id || 0)
    );

/**
 * One unit's children in the learner's order: its lessons, role plays, info pages **and units**,
 * interleaved. A unit inside this one arrives as a bare reference — `{content_type, id, order}` —
 * which is its position and nothing else; its title and its own children are its entry in the
 * course's flat `units` array, reached with {@link getUnitById}.
 */
export const buildUnitContentTree = (unit) => {
  return [...(unit?.content_items || [])]
    .filter((item) => item?.id != null)
    .map((item) => ({
      ...item,
      type: item.content_type === "info_page" ? "info-page" : item.content_type,
    }))
    .sort(
      (a, b) =>
        Number(a?.order || 1) - Number(b?.order || 1) ||
        Number(a?.id || 0) - Number(b?.id || 0) ||
        (CHILD_KIND_RANK[a?.type] ?? 0) - (CHILD_KIND_RANK[b?.type] ?? 0)
    );
};

export const getLocalizedEntityTitle = (entity, language, fallback = "Untitled") => {
  if (!entity || typeof entity !== "object") return fallback;

  const i18nMap = entity.name_i18n || entity.title_i18n || entity.label_i18n;
  const baseValue = entity.name || entity.title || entity.label || "";
  const lang = normalizeLanguageKey(language);
  if (!lang) {
    return String(baseValue || "").trim() || fallback;
  }

  const localizedValue = getI18nValueForLanguage(i18nMap, lang);
  if (String(localizedValue || "").trim()) {
    return String(localizedValue).trim();
  }

  // Legacy single-field content often stores English only on the base `name`/`title` field.
  if (lang === "english") {
    const base = String(baseValue || "").trim();
    if (base) return base;
    return fallback;
  }

  // Missing translation for this language: do not substitute English or another locale.
  return "";
};

export const getContentLabel = (item, language) => {
  const prefix = CONTENT_TYPE_PREFIX[item?.type] || "";
  const label = getLocalizedEntityTitle(item, language, "") || "Untitled";
  return prefix ? `${prefix}: ${label}` : label;
};

/**
 * Every unit of the course, at every depth, in one flat array — the shape the server sends and the
 * shape every tree walker here reads. A unit says which unit it is inside with `parent_unit_id`.
 */
export const getCourseUnits = (course) => {
  if (Array.isArray(course?.units)) return course.units;
  return [];
};

/** The units of the course itself: the sessions the learner picks from, in order. */
export const getTopLevelUnits = (course) =>
  sortByOrder(getCourseUnits(course).filter((unit) => unit?.parent_unit_id == null));

/** One unit of the flat list, by id. */
export const getUnitById = (course, unitId) => {
  if (unitId == null) return null;
  const target = String(unitId);
  return getCourseUnits(course).find((unit) => String(unit?.id) === target) || null;
};

/**
 * The units inside this one, in the order its own content list puts them — a group's place among
 * its siblings is the reference in the parent's items, not the group's own `order` read alone.
 */
export const getSubUnits = (course, unitId) => {
  const parent = getUnitById(course, unitId);
  if (!parent) return [];
  return buildUnitContentTree(parent)
    .filter((item) => item.type === "unit")
    .map((item) => getUnitById(course, item.id))
    .filter(Boolean);
};

/**
 * The units this one is inside, outermost first. Empty for a unit of the course. Stops on a missing
 * link or a cycle rather than looping, since the chain comes from server data the editor holds.
 */
export const getUnitAncestors = (course, unitId) => {
  const ancestors = [];
  const seen = new Set();
  let current = getUnitById(course, unitId);
  while (current?.parent_unit_id != null) {
    const key = String(current.parent_unit_id);
    if (seen.has(key)) break;
    seen.add(key);
    const parent = getUnitById(course, current.parent_unit_id);
    if (!parent) break;
    ancestors.unshift(parent);
    current = parent;
  }
  return ancestors;
};

/** How deep a unit sits: 1 for a unit of the course, 2 for a unit inside one. */
export const getUnitLevel = (course, unitId) => getUnitAncestors(course, unitId).length + 1;

/** How deep units may nest. Mirrors `MAX_UNIT_LEVELS` in `main/models/unit_tree.py`. */
export const MAX_UNIT_LEVELS = 2;

export const getDefaultContentEditorLanguage = (...sources) => {
  const candidates = [];

  sources.forEach((source) => {
    if (!source) return;

    if (typeof source === "string") {
      candidates.push(source);
      return;
    }

    if (Array.isArray(source)) {
      candidates.push(...source);
      return;
    }

    if (typeof source === "object") {
      if (typeof source.displayLanguage === "string") {
        candidates.push(source.displayLanguage);
      }
      if (typeof source.language === "string") {
        candidates.push(source.language);
      }
      if (Array.isArray(source.supportedLanguages)) {
        candidates.push(...source.supportedLanguages);
      }
      if (Array.isArray(source.supported_languages)) {
        candidates.push(...source.supported_languages);
      }
    }
  });

  for (const candidate of candidates) {
    const normalized = normalizeContentEditorLanguage(candidate);
    if (normalized) return normalized;
  }

  return "english";
};

/**
 * Keep only the entries of an i18n map whose language is in `supportedLanguages`,
 * preserving keys that are PRESENT even when their value is an empty string.
 *
 * An explicit "" is how the editor clears a single language: the server treats a
 * present-but-empty value as an explicit clear (and an absent key as "unchanged"). A
 * truthy filter would drop the "" and leave the stale translation in place, so we filter
 * on presence, not truthiness.
 */
export const pickSupportedI18n = (i18nMap, supportedLanguages = []) => {
  const filtered = {};
  (supportedLanguages || []).forEach((lang) => {
    if (i18nMap && Object.prototype.hasOwnProperty.call(i18nMap, lang)) {
      filtered[lang] = i18nMap[lang] ?? "";
    }
  });
  return filtered;
};

export const formatSavedAgo = (seconds) => {
  if (seconds < 1) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.floor(seconds / 60)}m ago`;
};
